import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  CheckCircleIcon,
  CheckIcon,
  CloudArrowUpIcon,
  DatabaseIcon,
  FileMagnifyingGlassIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import classNames from 'classnames'
import { Fragment, useState } from 'react'
import { match } from 'ts-pattern'
import { pluralize } from '../../utils/text-utils.ts'
import { TooltipWithHover } from '../tooltip.tsx'
import {
  formatFileSize,
  getDocType,
  getDocTypeBadgeClass,
  PHASE_PROGRESS,
  type UploadJob,
} from './coding-guidelines-types.ts'
import { DocTypeIcon } from './doc-type-icon.tsx'
import * as styles from './upload-activity-card.css.ts'

type UploadActivityCardProps = {
  jobs: UploadJob[]
  onDismiss: (jobId: string) => unknown
  onDismissAll: () => unknown
  onRetry: (jobId: string) => unknown
}

type SummarySegment = { key: string; label: string; kind: 'inflight' | 'ready' | 'failed' }

export const UploadActivityCard: React.FC<UploadActivityCardProps> = ({ jobs, onDismiss, onDismissAll, onRetry }) => {
  const [isCollapsed, setIsCollapsed] = useState(false)

  if (jobs.length === 0) {
    return null
  }

  const inFlightCount = jobs.filter(j => j.status.phase !== 'ready' && j.status.phase !== 'failed').length
  const readyCount = jobs.filter(j => j.status.phase === 'ready').length
  const failedCount = jobs.filter(j => j.status.phase === 'failed').length
  const areAllTerminal = inFlightCount === 0

  const summarySegments: SummarySegment[] = []
  if (inFlightCount > 0) {
    summarySegments.push({
      key: 'inflight',
      label: `${inFlightCount} ${pluralize(inFlightCount, 'document', 'documents')} processing`,
      kind: 'inflight',
    })
  }
  if (readyCount > 0) {
    summarySegments.push({ key: 'ready', label: `${readyCount} ready`, kind: 'ready' })
  }
  if (failedCount > 0) {
    summarySegments.push({ key: 'failed', label: `${failedCount} failed`, kind: 'failed' })
  }

  return (
    <section className={styles.card} aria-label="Upload activity">
      <header className={styles.header} onClick={() => setIsCollapsed(c => !c)}>
        <div className={styles.headerLeft}>
          <span className={styles.headerTitle}>Upload activity</span>
          <div className={styles.summary}>
            {summarySegments.map((seg, i) => (
              <Fragment key={seg.key}>
                {i > 0 ? (
                  <span className={styles.summarySep} aria-hidden>
                    ·
                  </span>
                ) : null}
                <span
                  className={match(seg.kind)
                    .with('ready', () => styles.summarySegmentReady)
                    .with('failed', () => styles.summarySegmentFailed)
                    .with('inflight', () => styles.summarySegment)
                    .exhaustive()}
                >
                  {match(seg.kind)
                    .with('ready', () => <CheckIcon size={12} weight="bold" className={styles.readyCheckIcon} />)
                    .with('failed', () => <WarningCircleIcon size={12} weight="fill" />)
                    .with('inflight', () => <span className={styles.pixeeSpinnerSm} aria-hidden />)
                    .exhaustive()}
                  {seg.label}
                </span>
              </Fragment>
            ))}
          </div>
        </div>
        <div className={styles.headerRight}>
          {areAllTerminal ? (
            <button
              type="button"
              className={styles.dismissAllBtn}
              aria-label="Dismiss all"
              onClick={e => {
                e.stopPropagation()
                onDismissAll()
              }}
            >
              Dismiss all
            </button>
          ) : null}
          <button
            type="button"
            className={styles.toggleButton}
            aria-expanded={!isCollapsed}
            aria-controls="upload-activity-list"
            aria-label={isCollapsed ? 'Expand upload activity' : 'Collapse upload activity'}
            onClick={e => {
              e.stopPropagation()
              setIsCollapsed(c => !c)
            }}
          >
            <CaretDownIcon size={16} weight="light" />
          </button>
        </div>
      </header>
      {!isCollapsed ? (
        <ul id="upload-activity-list" className={styles.list}>
          {jobs.map(job => (
            <UploadActivityRow key={job.id} job={job} onDismiss={onDismiss} onRetry={onRetry} />
          ))}
        </ul>
      ) : null}
    </section>
  )
}

type UploadActivityRowProps = {
  job: UploadJob
  onDismiss: (jobId: string) => unknown
  onRetry: (jobId: string) => unknown
}

const UploadActivityRow: React.FC<UploadActivityRowProps> = ({ job, onDismiss, onRetry }) => {
  const isReady = job.status.phase === 'ready'
  const isFailed = job.status.phase === 'failed'
  const isTerminal = isReady || isFailed
  const progressPercent = getProgressPercent(job)
  const statusText = getStatusText(job)

  return (
    <li className={classNames(styles.row, isReady && styles.rowReady)}>
      <div className={getIconBadgeClass(job.filename)} aria-hidden>
        <DocTypeIcon docType={getDocType(job.filename)} size={18} />
      </div>
      <div className={styles.body}>
        <div className={styles.rowHead}>
          <span className={styles.rowName} title={job.displayName}>
            {job.displayName}
          </span>
          {job.fileSizeBytes !== undefined ? (
            <span className={styles.rowMeta}>{formatFileSize(job.fileSizeBytes)}</span>
          ) : null}
        </div>
        <div className={styles.statusLine}>
          {match(job.status.phase)
            .with('failed', () => <WarningCircleIcon size={14} weight="fill" className={styles.failedStatusIcon} />)
            .with('ready', () => <CheckCircleIcon size={14} weight="fill" className={styles.readyCheckIcon} />)
            .with('uploading', () => (
              <CloudArrowUpIcon size={14} weight="light" className={styles.liveStatusIcon} aria-hidden />
            ))
            .with('reading', () => (
              <FileMagnifyingGlassIcon size={14} weight="light" className={styles.liveStatusIcon} aria-hidden />
            ))
            .with('indexing', () => (
              <DatabaseIcon size={14} weight="light" className={styles.liveStatusIcon} aria-hidden />
            ))
            .exhaustive()}
          <span className={isFailed ? styles.statusTextFailed : styles.statusText}>{statusText}</span>
        </div>
        {!isTerminal ? (
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
          </div>
        ) : null}
      </div>
      {isTerminal ? (
        <div className={styles.rowActions}>
          {isFailed ? (
            <button type="button" className={styles.retryBtn} onClick={() => onRetry(job.id)}>
              <ArrowClockwiseIcon size={14} weight="light" />
              Retry
            </button>
          ) : null}
          <TooltipWithHover
            trigger={
              <button type="button" className={styles.actionBtn} aria-label="Dismiss" onClick={() => onDismiss(job.id)}>
                <XIcon size={16} weight="light" />
              </button>
            }
          >
            Dismiss
          </TooltipWithHover>
        </div>
      ) : null}
    </li>
  )
}

const getProgressPercent = (job: UploadJob): number =>
  match(job.status)
    .with({ phase: 'uploading' }, s => Math.max(2, s.progressPercent))
    .with({ phase: 'reading' }, () => PHASE_PROGRESS.reading)
    .with({ phase: 'indexing' }, () => PHASE_PROGRESS.indexing)
    .with({ phase: 'ready' }, () => PHASE_PROGRESS.ready)
    .with({ phase: 'failed' }, () => 0)
    .exhaustive()

const formatMB = (bytes: number): string => (bytes / (1024 * 1024)).toFixed(1)

const getStatusText = (job: UploadJob): string =>
  match(job.status)
    .with({ phase: 'uploading' }, s => {
      if (job.fileSizeBytes !== undefined) {
        const uploadedBytes = (job.fileSizeBytes * s.progressPercent) / 100
        return `Uploading ${formatMB(uploadedBytes)} MB of ${formatMB(job.fileSizeBytes)} MB`
      }
      return `Uploading… ${s.progressPercent}%`
    })
    .with({ phase: 'reading' }, () => 'Reading your document…')
    .with({ phase: 'indexing' }, () => 'Indexing…')
    .with({ phase: 'ready' }, () => 'Ready. Pixee will consult this document during SAST triage and fix.')
    .with({ phase: 'failed' }, s => s.errorMessage ?? "Couldn't process this document.")
    .exhaustive()

const getIconBadgeClass = (filename: string): string =>
  getDocTypeBadgeClass({
    docType: getDocType(filename),
    classes: {
      base: styles.iconBadge,
      pdf: styles.iconBadgePdf,
      docx: styles.iconBadgeDocx,
      md: styles.iconBadgeMd,
      txt: styles.iconBadgeTxt,
      html: styles.iconBadgeHtml,
    },
  })
